import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { format, subDays } from 'date-fns';

import { TransactionPaymentMethod, TransactionStatus } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { mergeTransactionConnectionResults } from 'features/Bank/pages/Overview/Transactions/utils';
import { useBankTransferContext } from 'features/Payments/Pages/Transfers/contexts';

import { SearchBankTransfersDocument } from './graphql/searchBankTransfers.gql';
import { someDuplicateTransfer } from './someDuplicateTransfer';

export const useIsDuplicateTransfer = () => {
  const { companyProfileId } = useCompanyContext();
  const {
    bankTransfer: { amount, bankTransferRecipientId, fromBankAccountId },
  } = useBankTransferContext('confirmExternalDetails');
  const [isDuplicateTransfer, setIsDuplicateTransfer] = useState(false);

  const { data, error, fetchMore, loading, refetch } = useQuery(
    SearchBankTransfersDocument,
    {
      fetchPolicy: 'network-only',
      skip: !companyProfileId,
      variables: {
        after: null,
        companyProfileId: companyProfileId as string,
        filters: {
          afterDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
          bankAccountId: fromBankAccountId,
          paymentMethod: [
            TransactionPaymentMethod.InstantTransfer,
            TransactionPaymentMethod.Transfer,
          ],
          status: [TransactionStatus.Validated, TransactionStatus.Pending],
          valueGreaterThan: amount - 1,
          valueLesserThan: amount + 1,
        },
        first: 100,
      },
    },
  );

  const bankTransferEdges = data?.viewer.company.transactionsSearch.edges;
  const hasNextPage =
    data?.viewer.company.transactionsSearch.pageInfo.hasNextPage;
  const nextCursor =
    data?.viewer.company.transactionsSearch.pageInfo.nextCursor;

  useEffect(() => {
    if (!bankTransferEdges) {
      return;
    }

    setIsDuplicateTransfer(
      someDuplicateTransfer({
        amount,
        bankTransferEdges,
        bankTransferRecipientId,
      }),
    );
  }, [amount, bankTransferRecipientId, bankTransferEdges]);

  useEffect(() => {
    if (isDuplicateTransfer || !hasNextPage) {
      return;
    }

    fetchMore({
      updateQuery: (previousResult, { fetchMoreResult }) =>
        mergeTransactionConnectionResults(previousResult, fetchMoreResult),
      variables: {
        after: nextCursor,
      },
    });
  }, [fetchMore, hasNextPage, nextCursor, isDuplicateTransfer]);

  return {
    error,
    isDuplicateTransfer,
    loading: loading,
    refetch,
  };
};
